import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Table,
  Badge,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { api } from 'api';
import { formatDate } from 'common/utils';
import { CURRENCY } from 'common/constants';
import { FaSync, FaCoins, FaReceipt } from 'react-icons/fa';
import { PiHandWithdrawFill } from 'react-icons/pi';
import { Pagination } from 'components';
import 'pages/withdraw/_withdraw.scss';

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  paid: 'Paid',
  rejected: 'Rejected',
};

const STATUS_VARIANT = {
  pending: 'warning',
  approved: 'info',
  paid: 'success',
  rejected: 'danger',
};

const Withdraw = () => {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [balanceRes, requestsRes] = await Promise.all([
        api.getWithdrawalBalance(),
        api.getWithdrawalRequests({ page: currentPage, page_size: itemsPerPage }),
      ]);
      setBalance(balanceRes);
      // Support paginated { count, results } or legacy array
      if (requestsRes && typeof requestsRes === 'object' && Array.isArray(requestsRes.results)) {
        setRequests(requestsRes.results);
        setTotalCount(Number(requestsRes.count) ?? requestsRes.results.length);
      } else {
        const list = Array.isArray(requestsRes) ? requestsRes : [];
        setRequests(list);
        setTotalCount(list.length);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load withdrawal data.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => setItemsPerPage(num);

  const handleRequestWithdraw = async () => {
    if (!balance?.net_amount || balance.net_amount <= 0) {
      toast.warning('No withdrawable balance.');
      return;
    }
    setSubmitting(true);
    try {
      await api.createWithdrawalRequest();
      toast.success('Withdrawal request submitted successfully.');
      loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create withdrawal request.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Container fluid className="withdraw-page">
        <Alert variant="danger" className="withdraw-error mt-4">
          {error}
          <Button variant="outline-danger" size="sm" className="ms-2" onClick={loadData}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="withdraw-page">
      <div className="withdraw-header mb-4">
        <h2 className="d-flex align-items-center gap-2">
          <PiHandWithdrawFill />
          Withdraw Request
        </h2>
        <p className="text-muted mb-0">
          Request payout for earnings from online paid bookings and challenges. Only completed
          online payments are included.
        </p>
      </div>

      {loading ? (
        <div className="withdraw-loading">
          <Spinner animation="border" role="status" />
          <p>Loading...</p>
        </div>
      ) : (
        <>
          {/* Balance card */}
          <Card className="withdraw-balance-card mb-4">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span className="d-flex align-items-center gap-2">
                <FaCoins />
                Available to withdraw
              </span>
              <Button variant="outline-secondary" size="sm" onClick={loadData} disabled={loading}>
                <FaSync className="me-1" />
                Refresh
              </Button>
            </Card.Header>
            <Card.Body>
              {balance?.net_amount > 0 ? (
                <>
                  <Row>
                    <Col md={4}>
                      <div className="balance-row">
                        <span className="label">
                          Net amount
                          {balance.platform_fee_percentage != null
                            ? ` (after ${Number(balance.platform_fee_percentage)}% fee)`
                            : ' (after platform fee)'}
                        </span>
                        <span className="value text-success">
                          {CURRENCY} {parseFloat(balance.net_amount).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="balance-row">
                        <span className="label">Total gross</span>
                        <span className="value">
                          {CURRENCY} {parseFloat(balance.total_gross).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="balance-row">
                        <span className="label">
                          Platform fee
                          {balance.platform_fee_percentage != null
                            ? ` (${Number(balance.platform_fee_percentage)}%)`
                            : ''}
                        </span>
                        <span className="value text-muted">
                          {CURRENCY} {parseFloat(balance.platform_fee).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </Col>
                  </Row>
                  <div className="mt-3">
                    <span className="text-muted me-2">
                      {balance.transactions_count} online payment(s) included
                    </span>
                    <Button
                      variant="primary"
                      onClick={handleRequestWithdraw}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <Spinner animation="border" size="sm" className="me-2" />
                      ) : null}
                      Request withdrawal
                    </Button>
                  </div>
                  {balance.breakdown?.length > 0 && (
                    <div className="breakdown mt-3 pt-3 border-top">
                      <small className="text-muted d-block mb-2">By ground</small>
                      <Row>
                        {balance.breakdown.map((b) => (
                          <Col key={b.ground_id} md={4} className="mb-2">
                            <div className="small">
                              <strong>{b.ground_name}</strong>: {CURRENCY}{' '}
                              {Number(b.net).toLocaleString('en-PK', { minimumFractionDigits: 2 })}{' '}
                              ({b.transactions_count} tx)
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </>
              ) : (
                <div className="withdraw-empty-state">
                  <p className="mb-2">No withdrawable balance.</p>
                  <p className="small">
                    Complete more online paid bookings or challenges to see balance here.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* My requests */}
          <Card className="withdraw-requests-card">
            <Card.Header className="d-flex align-items-center gap-2">
              <FaReceipt />
              My withdrawal requests
            </Card.Header>
            <Card.Body className="p-0">
              {requests.length === 0 ? (
                <p className="withdraw-empty-requests mb-0">No withdrawal requests yet.</p>
              ) : (
                <div className="withdraw-table-container">
                  <Table responsive hover className="withdraw-table mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Net amount</th>
                        <th>Gross / Fee</th>
                        <th>Transactions</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((r) => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td>{formatDate(r.created)}</td>
                          <td>
                            <strong>
                              {CURRENCY} {parseFloat(r.amount).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                          <td className="small text-muted">
                            {CURRENCY} {parseFloat(r.total_gross).toLocaleString('en-PK', { minimumFractionDigits: 2 })}{' '}
                            / {CURRENCY} {parseFloat(r.platform_fee).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                          </td>
                          <td>{r.items_count}</td>
                          <td>
                            <Badge bg={STATUS_VARIANT[r.status] || 'secondary'}>
                              {STATUS_LABELS[r.status] || r.status}
                            </Badge>
                          </td>
                          <td className="small">{r.admin_notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>

          {totalCount > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={totalCount}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default Withdraw;
